import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";

import { resetLoginPopupCheck } from "@/lib/loginPopup";
import type { Branch, Role, Tenant, User } from "@/types/domain";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  tenant: Tenant | null;
  /**
   * The workspace a Super Admin has opened from the platform Tenants list.
   * Distinct from `tenant` (which comes from the JWT and is null for a
   * platform account): this is a client-side choice, sent to the API as the
   * X-Tenant-ID header the backend already authorizes for super admins only
   * (see internal/transport/http/middleware/auth.go). Cleared on sign-out and
   * on explicitly exiting the workspace.
   */
  impersonatedTenant: Tenant | null;
  /**
   * The workspace-access session id (WorkspaceAccessSession.id) opened for
   * `impersonatedTenant` - requirement 5 of strict tenant isolation. Set
   * alongside `impersonatedTenant` and cleared alongside it, so the two are
   * always either both present or both absent; kept separate from
   * `impersonatedTenant` itself (rather than folded into it) because it is
   * a client-side session handle, not part of the Tenant record.
   */
  workspaceSessionId: string | null;
  /**
   * The Tenant Admin's session-scoped "active branch" (Multi-Branch Tenant
   * Admin flow) - a client-side choice, sent to the API as the X-Branch-ID
   * header the backend only honours for role "tenant_admin" (see
   * establish()'s X-Branch-ID block, sibling to the X-Tenant-ID block
   * impersonatedTenant already uses). Purely a view filter, never a grant -
   * a Tenant Admin already has access to every branch in their own tenant.
   * Cleared on sign-out; re-validated against the tenant's current branch
   * list on every login/session load (see RequireActiveBranch.tsx).
   */
  activeBranch: Branch | null;
  setSession: (args: { accessToken: string; refreshToken: string; user: User; tenant?: Tenant | null }) => void;
  setAccessToken: (token: string) => void;
  /** Replaces only the signed-in user's permission list (after a token refresh picked up an access change). */
  setPermissions: (permissions: string[]) => void;
  setUser: (user: User) => void;
  /**
   * Replaces the workspace record the UI renders from - the tenant name and
   * logo in the sidebar, primarily.
   *
   * Needed because the company profile (including its logo) is edited
   * through a react-query resource (qk.settings.company) while the sidebar
   * reads this store, which is only ever populated at login. Without this,
   * uploading a new logo updated the settings page's own preview but the
   * sidebar kept the old one until the next sign-in.
   *
   * Writes to whichever slot is actually in scope: a Super Admin who has
   * opened a workspace is looking at `impersonatedTenant`, everyone else at
   * `tenant`. Mirrors useActiveTenant's own resolution so the two can never
   * disagree about which record the UI is showing.
   */
  setActiveTenant: (tenant: Tenant) => void;
  enterTenant: (tenant: Tenant, sessionId: string) => void;
  exitTenant: () => void;
  setActiveBranch: (branch: Branch) => void;
  clearActiveBranch: () => void;
  signOut: () => void;
}

/**
 * Storage backing the persisted auth store below.
 *
 * The bug this fixes: the store used to persist straight to `localStorage`,
 * which every tab of the same origin shares. One shared key meant logging
 * into a second account in another tab silently overwrote the first tab's
 * session, and either tab's `signOut()` (e.g. the dead-refresh-token 401
 * path in lib/api/client.ts) nulled it out from under the other - "second
 * login/logout anywhere kills whoever was there first."
 *
 * The fix is NOT a plain switch to `sessionStorage`: that alone would make
 * every brand-new tab start signed out (sessionStorage is never shared, not
 * even by a fresh tab opened seconds after a real login elsewhere), which is
 * both a real convenience regression and - just as importantly for this
 * codebase - breaks the e2e suite's whole session-caching design: `auth.
 * setup.ts` logs in once per role and Playwright's `storageState` restores
 * only `localStorage`/cookies into a fresh browser context, never
 * `sessionStorage`, so every one of the ~30 spec files that does
 * `test.use({ storageState: ROLE_ACCOUNTS.x.storageState })` would silently
 * start unauthenticated.
 *
 * So `sessionStorage` is this tab's live, authoritative copy - every read
 * after the first goes there, and it is the only thing `setAccessToken`/
 * `signOut`/`setSession` effectively act on as far as THIS tab's own
 * behaviour is concerned. `localStorage` is kept only as a one-time
 * bootstrap seed for a tab that has never hydrated yet: `getItem` falls
 * back to it exactly once (when this tab's own `sessionStorage` is still
 * empty), which is what lets a genuinely new tab inherit whoever was
 * logged in most recently, elsewhere - and is what keeps the e2e fixture
 * flow above working unmodified, since Playwright populates `localStorage`
 * before the app's first script runs. After that first read, a tab never
 * looks at `localStorage` again, so nothing another tab does to it can ever
 * change this tab's session mid-flight - that's what actually fixes the
 * reported bug. `removeItem` only clears the shared seed if it still holds
 * exactly the value this tab is removing, so one tab signing out can never
 * erase a different tab's more recent login out from under it.
 */
// Exported for direct unit coverage (auth-store.test.ts) - the store itself
// only exercises this indirectly through persist's own (debounced,
// implementation-detail) write path.
export const tabScopedWithSharedSeed: StateStorage = {
  getItem: (name) => {
    const own = sessionStorage.getItem(name);
    if (own !== null) return own;
    try {
      const seed = localStorage.getItem(name);
      if (seed !== null) sessionStorage.setItem(name, seed);
      return seed;
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    sessionStorage.setItem(name, value);
    try {
      localStorage.setItem(name, value);
    } catch {
      // The shared bootstrap seed is a convenience, not a requirement - a
      // full/disabled localStorage (private browsing, quota) must not stop
      // this tab's own session from working.
    }
  },
  removeItem: (name) => {
    try {
      if (localStorage.getItem(name) === sessionStorage.getItem(name)) {
        localStorage.removeItem(name);
      }
    } catch {
      // same rationale as setItem above.
    }
    sessionStorage.removeItem(name);
  },
};

/**
 * Persisted auth state. Only the tokens and a display-oriented copy of the
 * user/tenant live here; access control decisions always re-check
 * `user.permissions` at the point of use rather than caching a derived
 * boolean, so a role change on the backend takes effect on the next request
 * without a stale client-side flag.
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      tenant: null,
      impersonatedTenant: null,
      workspaceSessionId: null,
      activeBranch: null,
      setSession: ({ accessToken, refreshToken, user, tenant }) => {
        // A fresh login re-arms the Events & Announcements login popup.
        resetLoginPopupCheck();
        set({
          accessToken, refreshToken, user, tenant: tenant ?? null,
          impersonatedTenant: null, workspaceSessionId: null, activeBranch: null,
        });
      },
      setAccessToken: (token) => set({ accessToken: token }),
      setPermissions: (permissions) =>
        set((state) => (state.user ? { user: { ...state.user, permissions } } : {})),
      setUser: (user) => set({ user }),
      setActiveTenant: (tenant) =>
        set((state) => (state.impersonatedTenant ? { impersonatedTenant: tenant } : { tenant })),
      enterTenant: (tenant, sessionId) => set({ impersonatedTenant: tenant, workspaceSessionId: sessionId }),
      exitTenant: () => set({ impersonatedTenant: null, workspaceSessionId: null }),
      setActiveBranch: (branch) => set({ activeBranch: branch }),
      clearActiveBranch: () => set({ activeBranch: null }),
      signOut: () =>
        set({
          accessToken: null, refreshToken: null, user: null, tenant: null,
          impersonatedTenant: null, workspaceSessionId: null, activeBranch: null,
        }),
    }),
    { name: "hrms-auth", storage: createJSONStorage(() => tabScopedWithSharedSeed) },
  ),
);

/** The tenant currently in scope for API calls and UI: the JWT tenant for a
 * workspace login, or the workspace a Super Admin has opened, whichever
 * applies. */
export function useActiveTenant(): Tenant | null {
  const tenant = useAuthStore((s) => s.tenant);
  const impersonatedTenant = useAuthStore((s) => s.impersonatedTenant);
  return tenant ?? impersonatedTenant;
}

/**
 * Whether the signed-in user holds a permission.
 *
 * Resolves the same tri-state the backend does (domain.User.Can and
 * appctx.Identity.Can): an entry prefixed with "!" is an explicit DENY from the
 * per-login Manage Permissions flow and beats any grant, including a module
 * wildcard; a bare entry is a grant; no entry means the role decides, which the
 * backend has already folded into this list. Keeping the three implementations
 * identical matters - a client that shows an action the API refuses, or hides
 * one it allows, is a bug either way.
 *
 * This is a UX convenience only. Every one of these checks is re-made
 * server-side; hiding a button is never the security boundary.
 */
export function can(permission: string): boolean {
  const user = useAuthStore.getState().user;
  if (!user) return false;
  if (user.is_super_admin) return true;
  const [module] = permission.split(".");
  const matches = (held: string) => held === "*" || held === permission || held === `${module}.*`;
  if (user.permissions.some((p) => p.startsWith("!") && matches(p.slice(1)))) return false;
  return user.permissions.some((p) => !p.startsWith("!") && matches(p));
}

export function canAny(...permissions: string[]): boolean {
  return permissions.some((p) => can(p));
}

/**
 * Whether the signed-in user's role is one of the "finance-family" roles the
 * backend's GET /employees(/:id) hands the unmasked financeEmployeeResponse
 * projection to (handler/employee.go's isFinanceRole) instead of the
 * ordinary employee.salary-gated view. None of finance/payroll_admin/
 * finance_admin/payroll_specialist hold employee.salary - they get real
 * salary/bank/statutory figures through this separate mechanism instead, so
 * a component deciding whether to render employee.gross_monthly (etc.) must
 * check this too, not just can("employee.salary") alone, or it hides data
 * the API already sent. Mirrors the backend role list exactly - keep in
 * sync if that list changes.
 */
export function canSeeFinanceEmployeeData(): boolean {
  const user = useAuthStore.getState().user;
  if (!user) return false;
  return (
    can("employee.salary") ||
    ["finance", "payroll_admin", "finance_admin", "payroll_specialist"].includes(user.role)
  );
}

/**
 * Which of `roles` the signed-in user may assign to a new or existing
 * account. Mirrors the backend hierarchy check exactly
 * (UserService.authoriseRoleGrant) so the dropdown never offers an option
 * the API would reject - this is a UX convenience, not the security
 * boundary, which is enforced server-side regardless of what this returns.
 */
export function assignableRoles(roles: Role[]): Role[] {
  const user = useAuthStore.getState().user;
  if (!user) return [];
  if (user.is_super_admin) {
    return roles.filter((r) => r.slug !== "super_admin");
  }
  const actorRole = roles.find((r) => r.slug === user.role);
  if (!actorRole) return [];
  return roles.filter((r) => {
    if (r.slug === "super_admin") return false;
    // A deactivated custom role authorizes nothing (the backend refuses to mint
    // permissions from it and blocks its holders' logins), so offering it as an
    // assignment would create an account that cannot do anything. is_active is
    // absent on a legacy payload, which means active.
    if (r.is_active === false) return false;
    // Listed so existing holders stay resolvable (see canManageRole), but not
    // grantable right now - e.g. Telecaller while Call Analytics is off. The
    // API rejects the grant regardless; this just avoids offering it.
    if (r.assignable === false) return false;
    if (r.level <= actorRole.level) return false; // strict: equal or stronger is never assignable
    if (actorRole.slug === "hr") return r.slug === "employee" || r.slug === "telecaller";
    return true;
  });
}

/** Whether the signed-in user may act on an account currently holding
 * `targetRoleSlug` (suspend, delete, reset password, change role) - mirrors
 * UserService.authoriseAgainstRole. */
export function canManageRole(roles: Role[], targetRoleSlug: string): boolean {
  const user = useAuthStore.getState().user;
  if (!user) return false;
  if (user.is_super_admin) return true;
  const actorRole = roles.find((r) => r.slug === user.role);
  const targetRole = roles.find((r) => r.slug === targetRoleSlug);
  if (!actorRole || !targetRole) return false;
  return actorRole.level < targetRole.level;
}
